'use client';

import { cn } from '@reelplicate/ui/lib/utils';
import { Switch as SwitchPrimitive } from 'radix-ui';
import * as React from 'react';

/**
 * shadcn Switch (Radix), restyled to the Figma Toggle (Controls ↳ Toggle,
 * 48:117): a 36×20 radius/full track with 2px padding and a 16px bg/surface
 * knob with Shadow/SM.
 *
 * Off: border/strong, hover text/tertiary (Figma binds that token).
 * On: brand/strong, hover brand/strong-hover; the knob slides 16px right.
 * Focus: 2px border/focus outside stroke. Disabled: bg/disabled, or bg/emphasis
 * when on. Apps use `Toggle` (components/lib), which adds the label.
 */
function Switch({
  className,
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 items-center rounded-full px-0.5 outline-none',
        'bg-border-strong enabled:hover:bg-text-tertiary',
        'transition-[background-color,box-shadow] duration-[120ms] ease-out',
        'data-[state=checked]:bg-brand-strong data-[state=checked]:enabled:hover:bg-brand-strong-hover',
        'focus-visible:ring-2 focus-visible:ring-border-focus',
        'disabled:cursor-not-allowed data-disabled:bg-bg-disabled data-disabled:data-[state=checked]:bg-bg-emphasis',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className="bg-bg-surface pointer-events-none block size-4 rounded-full shadow-sm transition-transform duration-[120ms] ease-out data-[state=checked]:translate-x-4"
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
